import React, { Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useFormik } from "formik";
import { FieldArray, FormikProvider } from "formik";
import Stack from "@mui/material/Stack";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Divider from "@mui/material/Divider";
import TextField from "@mui/material/TextField";
import MuiSelect from "@mui/material/Select";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import {
  createAd28TaxiwayValidationSchema,
  Ad212SurfaceTypes,
  Ad212PavementTypes,
  Ad212EveluationTypes,
  Ad212MaxTyrePressureTypes,
  Ad212PavementSubgradeTypes,
} from "../../constants/ad2Data";
import {
  removeSelctedAirportFormCategories,
  taxiwayUpload,
} from "../../models/airport";
import { ACCESS_TOKEN } from "../../constants/constant";
import { getAsyncStorageValue } from "../../utils/localStorage";
import { PrimaryButton } from "../button";

const taxiwayDetail = {
  designation: "",
  width: "",
  surface: "",
  classPCN: "",
  pavementTypePCN: "",
  pavementSubgradePCN: "",
  maxTyrePressurePCN: "",
  evaluationMethodPCN: "",
};

export default function Ad28TaxiwayForm({ selectedAirport, selectedCategory }) {
  const dispatch = useDispatch();
  const isLoadingApronUpload = useSelector(
    (state) => state.airport.isLoadingApronUpload
  );

  const createAd28TaxiwayForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      taxiways: [taxiwayDetail],
    },
    validationSchema: createAd28TaxiwayValidationSchema,
    onSubmit: (values) => {
      const taxiwayValues = values.taxiways.map((apron) => {
        return apron;
      });

      getAsyncStorageValue(ACCESS_TOKEN).then((token) => {
        dispatch(
          taxiwayUpload(
            {
              airport_id: selectedAirport.id,
              form: taxiwayValues,
            },
            token,
            handleUploadSuccessCallback
          )
        );
      });
    },
  });

  const handleUploadSuccessCallback = () => {
    createAd28TaxiwayForm.resetForm();
    createAd28TaxiwayForm.setSubmitting(false);
    dispatch(removeSelctedAirportFormCategories(selectedCategory));
  };

  return (
    <Paper sx={{ pb: 2, pt: 2 }}>
      <FormikProvider value={createAd28TaxiwayForm}>
        <form onSubmit={createAd28TaxiwayForm.handleSubmit}>
          <FieldArray
            name="taxiways"
            render={(arrayHelpers) => (
              <div>
                {createAd28TaxiwayForm.values.taxiways.map((_, index) => {
                  const isTouched = createAd28TaxiwayForm.touched?.taxiways
                    ? createAd28TaxiwayForm.touched?.taxiways[index]
                    : false;
                  const isErrors = createAd28TaxiwayForm.errors?.taxiways
                    ? createAd28TaxiwayForm.errors?.taxiways[index]
                    : false;

                  return (
                    <Fragment key={index}>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ pl: 2, pr: 2 }}
                      >
                        <Box sx={{ width: "30%" }}>Designation</Box>
                        <Box sx={{ pl: 2 }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            name={`taxiways[${index}].designation`}
                            value={
                              createAd28TaxiwayForm.values.taxiways[index]
                                .designation
                            }
                            onChange={createAd28TaxiwayForm.handleChange}
                            error={
                              isTouched &&
                              isErrors &&
                              createAd28TaxiwayForm.touched?.taxiways[index]
                                .designation &&
                              Boolean(
                                createAd28TaxiwayForm.errors?.taxiways[index]
                                  .designation
                              )
                            }
                            helperText={
                              isErrors && isTouched
                                ? createAd28TaxiwayForm.errors?.taxiways[index]
                                    .designation
                                : ""
                            }
                            sx={{ mb: 2, mt: 2 }}
                          />
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ pl: 2, pr: 2 }}
                      >
                        <Box sx={{ width: "30%" }}>Width</Box>
                        <Box sx={{ pl: 2 }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            name={`taxiways[${index}].width`}
                            value={
                              createAd28TaxiwayForm.values.taxiways[index].width
                            }
                            onChange={createAd28TaxiwayForm.handleChange}
                            error={
                              isTouched &&
                              isErrors &&
                              createAd28TaxiwayForm.touched?.taxiways[index]
                                .width &&
                              Boolean(
                                createAd28TaxiwayForm.errors?.taxiways[index]
                                  .width
                              )
                            }
                            helperText={
                              isErrors && isTouched
                                ? createAd28TaxiwayForm.errors?.taxiways[index]
                                    .width
                                : ""
                            }
                            sx={{ mb: 2, mt: 2 }}
                          />
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ pl: 2, pr: 2 }}
                      >
                        <Box sx={{ width: "30%" }}>Surface</Box>
                        <Box sx={{ pl: 2 }}>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{ mb: 2, width: 200 }}
                            error={
                              isTouched &&
                              isErrors &&
                              createAd28TaxiwayForm.touched?.taxiways[index]
                                .surface &&
                              Boolean(
                                createAd28TaxiwayForm.errors?.taxiways[index]
                                  .surface
                              )
                            }
                          >
                            <MuiSelect
                              name={`taxiways[${index}].surface`}
                              value={
                                createAd28TaxiwayForm.values.taxiways[index]
                                  .surface
                              }
                              onChange={createAd28TaxiwayForm.handleChange}
                            >
                              {Ad212SurfaceTypes.map((types) => (
                                <MenuItem key={types.id} value={types.value}>
                                  {types.label}
                                </MenuItem>
                              ))}
                            </MuiSelect>
                            <FormHelperText>
                              {isErrors && isTouched
                                ? createAd28TaxiwayForm.errors?.taxiways[index]
                                    .surface
                                : ""}
                            </FormHelperText>
                          </FormControl>
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ pl: 2, pr: 2 }}
                      >
                        <Box sx={{ width: "30%" }}>Class PCN</Box>
                        <Box sx={{ pl: 2 }}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            size="small"
                            type="number"
                            name={`taxiways[${index}].classPCN`}
                            value={
                              createAd28TaxiwayForm.values.taxiways[index]
                                .classPCN
                            }
                            onChange={createAd28TaxiwayForm.handleChange}
                            error={
                              isTouched &&
                              isErrors &&
                              createAd28TaxiwayForm.touched?.taxiways[index]
                                .classPCN &&
                              Boolean(
                                createAd28TaxiwayForm.errors?.taxiways[index]
                                  .classPCN
                              )
                            }
                            helperText={
                              isErrors && isTouched
                                ? createAd28TaxiwayForm.errors?.taxiways[index]
                                    .classPCN
                                : ""
                            }
                            sx={{ mb: 2, mt: 2 }}
                            InputProps={{
                              endAdornment: (
                                <InputAdornment position="end">
                                  NUM
                                </InputAdornment>
                              ),
                            }}
                          />
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ pl: 2, pr: 2 }}
                      >
                        <Box sx={{ width: "30%" }}>Pavement Type PCN</Box>
                        <Box sx={{ pl: 2 }}>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{ mb: 2, width: 200 }}
                            error={
                              isTouched &&
                              isErrors &&
                              createAd28TaxiwayForm.touched?.taxiways[index]
                                .pavementTypePCN &&
                              Boolean(
                                createAd28TaxiwayForm.errors?.taxiways[index]
                                  .pavementTypePCN
                              )
                            }
                          >
                            <MuiSelect
                              name={`taxiways[${index}].pavementTypePCN`}
                              value={
                                createAd28TaxiwayForm.values.taxiways[index]
                                  .pavementTypePCN
                              }
                              onChange={createAd28TaxiwayForm.handleChange}
                            >
                              {Ad212PavementTypes.map((types) => (
                                <MenuItem key={types.id} value={types.value}>
                                  {types.label}
                                </MenuItem>
                              ))}
                            </MuiSelect>
                            <FormHelperText>
                              {isErrors && isTouched
                                ? createAd28TaxiwayForm.errors?.taxiways[index]
                                    .pavementTypePCN
                                : ""}
                            </FormHelperText>
                          </FormControl>
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ pl: 2, pr: 2 }}
                      >
                        <Box sx={{ width: "30%" }}>Pavement Subgrade PCN</Box>
                        <Box sx={{ pl: 2 }}>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{ mb: 2, width: 200 }}
                            error={
                              isTouched &&
                              isErrors &&
                              createAd28TaxiwayForm.touched?.taxiways[index]
                                .pavementSubgradePCN &&
                              Boolean(
                                createAd28TaxiwayForm.errors?.taxiways[index]
                                  .pavementSubgradePCN
                              )
                            }
                          >
                            <MuiSelect
                              name={`taxiways[${index}].pavementSubgradePCN`}
                              value={
                                createAd28TaxiwayForm.values.taxiways[index]
                                  .pavementSubgradePCN
                              }
                              onChange={createAd28TaxiwayForm.handleChange}
                            >
                              {Ad212PavementSubgradeTypes.map((types) => (
                                <MenuItem key={types.id} value={types.value}>
                                  {types.label}
                                </MenuItem>
                              ))}
                            </MuiSelect>
                            <FormHelperText>
                              {isErrors && isTouched
                                ? createAd28TaxiwayForm.errors?.taxiways[index]
                                    .pavementSubgradePCN
                                : ""}
                            </FormHelperText>
                          </FormControl>
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ pl: 2, pr: 2 }}
                      >
                        <Box sx={{ width: "30%" }}>Max Tyre Pressure</Box>
                        <Box sx={{ pl: 2 }}>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{ mb: 2, width: 200 }}
                            error={
                              isTouched &&
                              isErrors &&
                              createAd28TaxiwayForm.touched?.taxiways[index]
                                .maxTyrePressurePCN &&
                              Boolean(
                                createAd28TaxiwayForm.errors?.taxiways[index]
                                  .maxTyrePressurePCN
                              )
                            }
                          >
                            <MuiSelect
                              name={`taxiways[${index}].maxTyrePressurePCN`}
                              value={
                                createAd28TaxiwayForm.values.taxiways[index]
                                  .maxTyrePressurePCN
                              }
                              onChange={createAd28TaxiwayForm.handleChange}
                            >
                              {Ad212MaxTyrePressureTypes.map((types) => (
                                <MenuItem key={types.id} value={types.value}>
                                  {types.label}
                                </MenuItem>
                              ))}
                            </MuiSelect>
                            <FormHelperText>
                              {isErrors && isTouched
                                ? createAd28TaxiwayForm.errors?.taxiways[index]
                                    .maxTyrePressurePCN
                                : ""}
                            </FormHelperText>
                          </FormControl>
                        </Box>
                      </Stack>
                      <Stack
                        direction="row"
                        alignItems="center"
                        sx={{ pl: 2, pr: 2 }}
                      >
                        <Box sx={{ width: "30%" }}>Evaluation Method PCN</Box>
                        <Box sx={{ pl: 2 }}>
                          <FormControl
                            fullWidth
                            size="small"
                            sx={{ mb: 2, width: 200 }}
                            error={
                              isTouched &&
                              isErrors &&
                              createAd28TaxiwayForm.touched?.taxiways[index]
                                .evaluationMethodPCN &&
                              Boolean(
                                createAd28TaxiwayForm.errors?.taxiways[index]
                                  .evaluationMethodPCN
                              )
                            }
                          >
                            <MuiSelect
                              name={`taxiways[${index}].evaluationMethodPCN`}
                              value={
                                createAd28TaxiwayForm.values.taxiways[index]
                                  .evaluationMethodPCN
                              }
                              onChange={createAd28TaxiwayForm.handleChange}
                            >
                              {Ad212EveluationTypes.map((types) => (
                                <MenuItem key={types.id} value={types.value}>
                                  {types.label}
                                </MenuItem>
                              ))}
                            </MuiSelect>
                            <FormHelperText>
                              {isErrors && isTouched
                                ? createAd28TaxiwayForm.errors?.taxiways[index]
                                    .evaluationMethodPCN
                                : ""}
                            </FormHelperText>
                          </FormControl>
                        </Box>
                      </Stack>
                      <Stack alignItems="flex-end">
                        <PrimaryButton
                          label="Remove"
                          color="error"
                          onClick={() => arrayHelpers.remove(index)}
                        />
                      </Stack>
                      <Divider />
                    </Fragment>
                  );
                })}
                <PrimaryButton
                  label="Add More"
                  onClick={() => arrayHelpers.push(taxiwayDetail)}
                />
              </div>
            )}
          />
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            sx={{ pr: 2 }}
          >
            <PrimaryButton
              type="submit"
              label="Submit"
              isLoading={isLoadingApronUpload}
              disabled={isLoadingApronUpload}
              sx={{ width: 200 }}
            />
          </Stack>
        </form>
      </FormikProvider>
    </Paper>
  );
}
